/**
 * Dove sta l'API.
 *
 * Se `VITE_API_URL` non è impostata, l'indirizzo si **ricava dall'host su cui
 * la pagina è aperta**. È ciò che permette di provare l'app da un telefono
 * sulla stessa rete senza toccare nessuna configurazione: aprendo
 * `http://192.168.1.42:5173`, il frontend chiama `http://192.168.1.42:3000`.
 *
 * Con un valore fisso come `http://localhost:3000`, un telefono cercherebbe
 * l'API sul *proprio* localhost e non troverebbe niente.
 */
function resolveApiUrl(): string {
  // Dietro il tunnel l'API arriva dallo stesso host, inoltrata da Vite: una
  // base vuota significa «stessa origine», e con essa cookie e passkey
  // funzionano senza alcuna eccezione da configurare.
  if (import.meta.env.VITE_API_PROXY === "1") return "";

  const explicit = import.meta.env.VITE_API_URL;
  if (explicit) return explicit;

  if (typeof window === "undefined") return "http://localhost:3000";

  const port = import.meta.env.VITE_API_PORT ?? "3000";
  return `${window.location.protocol}//${window.location.hostname}:${port}`;
}

export const API_URL = resolveApiUrl();

/** Oltre questo tempo consideriamo l'API irraggiungibile. */
const REQUEST_TIMEOUT_MS = 5_000;

export class ApiError extends Error {
  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Interroga `/health` e valida la risposta contro il contratto condiviso.
 * Distingue tre modi di fallire — rete, HTTP, contratto — perché in diagnostica
 * "non funziona" non è un'informazione utile.
 */
export async function fetchHealth() {
  const { healthResponseSchema } = await import("@qota/shared");

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${API_URL}/health`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError(`Nessuna risposta da ${API_URL} entro ${REQUEST_TIMEOUT_MS / 1000}s`);
    }
    throw new ApiError(`Impossibile raggiungere ${API_URL}. L'API è avviata?`, error);
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    throw new ApiError(`L'API ha risposto ${response.status} ${response.statusText}`);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (error) {
    throw new ApiError("L'API ha risposto con un corpo non JSON", error);
  }

  const parsed = healthResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      `La risposta non rispetta il contratto: ${parsed.error.issues
        .map((issue) => `${issue.path.join(".")} ${issue.message}`)
        .join("; ")}`,
      parsed.error,
    );
  }

  return parsed.data;
}
